import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Cable, ShieldCheck, Zap } from 'lucide-react';

export default function Products() {
  return (
    <main className="min-h-screen bg-[#071426] text-white">
      <Helmet>
        <title>Products | SALAR Technologies LLC</title>
        <meta name="description" content="Explore SALAR Technologies physical-product programs, beginning with the patent-pending GPMI One Cable unified data and power interconnect architecture." />
        <meta property="og:title" content="SALAR Technologies Products" />
        <meta property="og:description" content="Engineering-development physical products built around reliability, verifiable channel identity, and controlled operation." />
        <meta property="og:url" content="https://www.salartechnologies.com/products" />
        <link rel="canonical" href="https://www.salartechnologies.com/products" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <section className="border-b border-blue-800/30 bg-[#0A1628] px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.28em] text-cyan-300">SALAR PRODUCT SYSTEMS</p>
          <h1 className="max-w-4xl text-4xl font-bold leading-tight md:text-6xl">Physical products built around evidence, reliability and controlled operation.</h1>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-blue-100">
            SALAR Technologies is translating selected patent-backed engineering programs into physical product architectures.
            Each product page separates verified facts from engineering targets so public material never outruns validation evidence.
          </p>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-8 lg:grid-cols-[1.05fr_.95fr]">
            <article className="overflow-hidden rounded-2xl border border-cyan-400/30 bg-[#0D203B] shadow-2xl">
              <div className="relative min-h-[320px] bg-cover bg-center" style={{backgroundImage:"linear-gradient(rgba(4,13,28,.32),rgba(4,13,28,.86)),url('/img/pexels_fiber_cables.jpg')"}}>
                <div className="absolute inset-0 flex flex-col justify-end p-8">
                  <div className="mb-3 flex flex-wrap gap-2">
                    <span className="rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 text-xs font-bold text-emerald-200">PATENT PENDING</span>
                    <span className="rounded-full border border-cyan-400/40 bg-cyan-400/10 px-3 py-1 text-xs font-bold text-cyan-200">ENGINEERING DEVELOPMENT</span>
                  </div>
                  <h2 className="text-4xl font-bold">GPMI One Cable</h2>
                  <p className="mt-3 max-w-2xl text-blue-100">Unified data + power cable architecture with plug-resident signed channel identity, physical fingerprint verification and sequenced power enablement.</p>
                </div>
              </div>
              <div className="grid gap-4 p-7 sm:grid-cols-3">
                {[
                  [ShieldCheck,'Signed channel identity','Authenticated assembly record'],
                  [Cable,'Physical verification','Impedance / electrical fingerprint'],
                  [Zap,'Controlled power-up','Verification-aware enable sequence'],
                ].map(([Icon,title,desc]) => (
                  <div key={title} className="rounded-xl border border-blue-700/30 bg-blue-950/40 p-4">
                    <Icon className="mb-3 text-cyan-300" size={23}/>
                    <h3 className="font-bold">{title}</h3>
                    <p className="mt-1 text-sm text-blue-200">{desc}</p>
                  </div>
                ))}
              </div>
              <div className="px-7 pb-7">
                <Link to="/products/gpmi-one-cable" className="inline-flex items-center gap-2 rounded-lg bg-cyan-500 px-5 py-3 font-bold text-[#071426] hover:bg-cyan-300">
                  Explore GPMI One Cable <ArrowRight size={17}/>
                </Link>
              </div>
            </article>

            <aside className="rounded-2xl border border-blue-700/30 bg-[#0D203B] p-8">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-blue-300">PRODUCT DISCLOSURE DISCIPLINE</p>
              <h2 className="mt-3 text-2xl font-bold">Engineering facts first.</h2>
              <p className="mt-4 leading-7 text-blue-100">
                SALAR product pages are public-safe engineering summaries. A listed patent filing does not mean a product is production-qualified,
                standards-certified or commercially released. Final electrical, mechanical, thermal, signal-integrity and compliance ratings are published only after the applicable validation gates close.
              </p>
              <div className="mt-7 rounded-xl border-l-4 border-[#D4A843] bg-[#132A47] p-5 text-sm leading-6 text-blue-100">
                <strong className="text-white">Naming note.</strong> “GPMI One Cable” is SALAR’s working public product name.
                GPMI is an industry interface reference. SALAR does not claim GPMI certification, standards-body endorsement or wire-level compatibility unless separately validated.
              </div>
              <p className="mt-8 text-sm text-blue-300">More SALAR product pages will be added only when their public-release and validation boundaries are ready.</p>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}
